import {
  Component,
  EventEmitter,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { FieldTypeConfig, FormlyFieldConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import {
  UploadOutput,
  UploadInput,
  UploadFile,
  humanizeBytes,
  UploaderOptions,
  UploadStatus,
} from 'ngx-uploader';
import { FormService } from '../../form.service';
import { forkJoin, Subscription } from 'rxjs';
import { cloneDeep, get, isArray, isEmpty, isNil, remove } from 'lodash';
import {
  ALLOWED_CONTENT_TYPES,
  ALLOWED_EXTENSIONS,
} from 'src/app/shared/constants';
import {
  ContactSelfServiceFormsService,
  IFileData,
} from '../../contact-self-service-forms/contact-self-service-forms.service';
import { take } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-formly-field-file',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FormlyFieldFileComponent
  extends FieldType<FormlyFieldConfig>
  implements OnInit
{
  uploaderOptions: UploaderOptions = {
    concurrency: 1,
    maxUploads: 3,
    maxFileSize: 15 * 1024 * 1024,
    allowedContentTypes: ALLOWED_CONTENT_TYPES,
  };

  formData: FormData;
  files: UploadFile[] = [];
  uploadInput: EventEmitter<UploadInput> = new EventEmitter<UploadInput>();
  humanizeBytes = humanizeBytes;
  dragOver: boolean;
  uploadStatus = UploadStatus;
  subs = new Subscription();
  errorMessage = '';
  fileTips;
  loaded: boolean = false; // to check if page is loaded or not
  enableDownload: boolean[] = []; // array containing boolean values of which files to be downloaded or not. If true, user can download it
  filesLoaded: boolean = false; // to check if files are loaded
  token: string = '';
  fieldTip: string = '';

  constructor(
    private formService: FormService,
    private contactSelfService: ContactSelfServiceFormsService,
    protected snackBar: MatSnackBar,
    private activatedRoute: ActivatedRoute,
    private cd: ChangeDetectorRef
  ) {
    super();
  }

  onFocusIn() {
    this.field.focus = true;
    this.stateChanges.next();
  }
  onFocusOut() {
    this.field.focus = false;
    if (this.files?.length === 0 && this.props?.required) {
      this.formControl.markAsTouched();
      this.formControl.markAsDirty();
      this.formControl.setErrors({ required: true });
    }
    this.stateChanges.next();
  }

  ngOnInit() {
    this.loaded = true;
    // get file fields name
    this.getFormControlName();
    this.activatedRoute.queryParams.subscribe((params) => {
      this.token = params['token'];
    });
    this.subs.add(
      this.formControl.valueChanges.subscribe((value) => {
        if (isNil(value)) {
          this.files = [];
          this.formControl.markAsUntouched();
          this.formControl.markAsPristine();
          this.cd.markForCheck();
        } else {
          /* store the file tips.
            here tip can be a string or array of strings
          */
          this.fileTips = value;
          if (!this.filesLoaded) {
            // call this method only when page is loaded
            this.getFileInfo();
          }
        }
      })
    );
  }

  // Get the file fields name which will be used as tip
  getFormControlName() {
    var parent = this.formControl['_parent'];

    // only such parent, which is FormGroup, has a dictionary with control-names as a key and a form-control as a value
    if (parent) {
      // now we will iterate those keys (i.e. names of controls)
      Object.keys(parent.controls).forEach((name) => {
        // compare the passed control and a child control of a parent - with provided name (we iterate them all)
        if (this.formControl === parent.controls[name]) {
          this.fieldTip = name;
        }
      });
    }
  }

  onUploadOutput(output: UploadOutput): void {
    switch (output.type) {
      case 'addedToQueue':
        if (typeof output.file !== 'undefined') {
          const isMultiSelect = get(
            this,
            'field.templateOptions.isMultiSelect',
            null
          );
          if (!isMultiSelect && this.files.length > 0) {
            this.files.pop();
          }
          this.files.push(output.file);
          const ext = output.file.name.split('.').pop();
          if (
            output.file.type == '' &&
            ALLOWED_EXTENSIONS.some((x) => x.name === ext)
          ) {
            output.file.type = ALLOWED_EXTENSIONS.find(
              (x) => x.name === ext
            ).value;
          }
          if (
            this.uploaderOptions.allowedContentTypes &&
            !this.uploaderOptions.allowedContentTypes.includes(output.file.type)
          ) {
            this.errorMessage = `File format not supported for upload.`;
            break;
          }
          this.formService.getUploadUrl$(output.file).subscribe(
            (data) => {
              output.file.form.set('reference', data.reference);
              const event: UploadInput = {
                type: 'uploadFile',
                url: data.uploadUrl,
                method: 'PUT',
                file: output.file,
                includeWebKitFormBoundary: false,
              };
              this.uploadInput.emit(event);
            },
            (err) => {
              console.log('Failed getting upload URL', {
                error: err,
                file: output.file,
              });
              output.file.progress.status = UploadStatus.Cancelled;
              output.file.responseStatus = 500;
              this.cd.markForCheck();
            }
          );
          this.updateValue();
        }
        break;
      case 'uploading':
        if (typeof output.file !== 'undefined') {
          const index = this.files.findIndex(
            (file) =>
              typeof output.file !== 'undefined' && file.id === output.file.id
          );
          this.files[index] = output.file;
        }
        break;
      case 'done':
        this.updateValue();
        break;
    }
  }

  removeFile(outputFile: UploadFile): void {
    /* Manually removing the files.
      This is because when the form is loaded with the values,
      uploadInput.emit({type: 'remove', id: oututFile.id, file: oututFile}) is not working
    */
    this.files = this.files.filter((file: UploadFile) => file !== outputFile);
    this.updateValue();
    const validFileType =
      isEmpty(this.files) ||
      (this.uploaderOptions.allowedContentTypes &&
        isEmpty(
          this.files.filter(
            (file) =>
              !this.uploaderOptions.allowedContentTypes.includes(file.type)
          )
        ));
    if (validFileType) {
      this.errorMessage = '';
    }
  }

  updateValue() {
    const isMultiSelect = get(
      this,
      'field.templateOptions.isMultiSelect',
      null
    );
    let references;
    if (isMultiSelect === false) {
      const file = this.files.find(
        (file) => file.progress.status === UploadStatus.Done
      );
      references = file ? file.form.get('reference') : '';
    } else {
      references = this.files
        .filter((file) => file.progress.status === UploadStatus.Done)
        .map((file) => file.form.get('reference'));
    }
    this.filesLoaded = true;
    this.loaded = true;
    // check if user can download the file
    this.checkForDownload(cloneDeep(references));
    this.formControl.setValue(this.files.length > 0 ? references : null);
    this.formControl.markAsTouched();
  }

  /**
   * Fetch details (name, data, size and mimeType) of a file via its tip.
   * All the file tips are stored in fileTips array.
   */
  getFileInfo(): void {
    this.loaded = false;
    const isMultiSelect = get(
      this,
      'field.templateOptions.isMultiSelect',
      null
    );
    const observables = [];
    if (isMultiSelect) {
      this.fileTips.map((fileTip) => {
        observables.push(this.contactSelfService.getFileData(fileTip));
      });
    } else {
      //here this.fileTips will always be a string
      observables.push(this.contactSelfService.getFileData(this.fileTips));
    }

    forkJoin(observables)
      .pipe(take(1))
      .subscribe(
        (results: IFileData[]) => {
          results.map((result: IFileData, index: number) => {
            const fileInfo = get(result, 'fileInfo', null);
            if (fileInfo) {
              const reference = isMultiSelect
                ? this.fileTips[index]
                : this.fileTips;
              const formData = new FormData();
              /* the reference when file is submitted is a unique id from S3 bucket,
              but below reference is the eno tip
            */
              formData.set('reference', reference);
              // Prepare file object
              const file: UploadFile = {
                id: reference,
                fileIndex: index + 1,
                form: formData,
                name: fileInfo?.name,
                progress: { status: this.uploadStatus.Done },
                lastModifiedDate: this.getDate(), // Giving random date, as it is a mandatory field
                size: fileInfo?.size,
                type: fileInfo?.mimeType,
              };
              this.files[index] = file;
            }
          });
          this.updateValue();
        },
        (err) => {
          this.loaded = true;
          const snackBarRef = this.snackBar.open(
            'Could not fetch file details'
          );
          snackBarRef
            .onAction()
            .pipe(take(1))
            .subscribe(() => snackBarRef.dismiss());
        }
      );
  }

  getDate(): Date {
    return new Date();
  }

  /**
   *
   * @param file - contains information such as reference of the file. Using this reference we can downlaod the file.
   *
   * This method performs the below functionalities -
   * 1. checks if the file can be downloaded by checking the progress of the file and whether enableDownload is set to true
   * 2. Once check passes, calls the downloadFile() method from contact self service.
   *  a. downloadFile() returns below reponses:
   *    i. if success, returns the file's download url
   *    ii. if failed, returns error response
   * 3. Once we have the url, open the url in another window to download the file
   * 4. If the url is empty, show error message
   */
  download(file: UploadFile, enableDownload: boolean): void {
    if (enableDownload) {
      const reference = file.form.get('reference') as string;
      this.contactSelfService
        .downloadFile(reference, this.fieldTip, this.token)
        .pipe(take(1))
        .subscribe(
          (url) => {
            const uri = get(url, 'downloadUrl.uri', null);
            if (uri) {
              window.open(uri, '_blank');
            } else {
              const snackBarRef = this.snackBar.open('Unable to download file');
              snackBarRef
                .onAction()
                .pipe(take(1))
                .subscribe(() => snackBarRef.dismiss());
            }
          },
          (err) => {
            const snackBarRef = this.snackBar.open('Unable to download file');
            snackBarRef
              .onAction()
              .pipe(take(1))
              .subscribe(() => snackBarRef.dismiss());
          }
        );
    }
  }

  /**
   *
   * @param references - it is either string or array of string depending whether file control is multi select or single select
   *
   * This method performs below functionalities -
   * 1. Checks if file control is multi select or not and
   * validates if the reference format is of type s3 bucket or of eno tip.
   * Stores the resulting boolean value in isS3Reference
   * 2. Uses global window's pathname property to find if the form is for registration or update
   * 3. If form is update and reference is not of s3 bucket, enable user to download the file
   */
  checkForDownload(references): void {
    if (references && references.length > 0) {
      // clean the existing array
      remove(this.enableDownload);
      const isMultiSelect = get(
        this,
        'field.templateOptions.isMultiSelect',
        null
      );

      const pathName = this.getPathName(); // wrapper method for spec files
      if (pathName) {
        if (isMultiSelect) {
          if (isArray(references)) {
            // checks if reference contains '-' which indicates if it belongs to s3 bucket
            references?.forEach((reference, i) => {
              this.enableDownload[i] =
                pathName?.indexOf('contact-form/update') > -1 &&
                reference?.indexOf('-') < 0;
            });
          }
        } else {
          // checks if reference contains '-' which indicates if it belongs to s3 bucket
          this.enableDownload[0] =
            pathName?.indexOf('contact-form/update') > -1 &&
            references?.indexOf('-') < 0;
        }
      }
    }
  }

  getPathName(): any {
    return get(window, 'location.pathname', null);
  }
}
